<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { useModal } from '@/composition/use-modal';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { WHATS_NEW_MODAL_NAME } from '@/domain/whats-new/whats-new.const';
  import V2WhatsNewClubSceneMenu from './V2WhatsNewClubSceneMenu.vue';
  import V2WhatsNewClubSceneRewards from './V2WhatsNewClubSceneRewards.vue';
  import V2WhatsNewClubSceneCode from './V2WhatsNewClubSceneCode.vue';

  withDefaults(defineProps<{ forcePreview?: boolean }>(), {
    forcePreview: false,
  });

  const TOTAL_STEPS = 3;

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  const step = ref(1);
  const copyFading = ref(false);
  const primaryButton = ref<InstanceType<typeof PosUiButton> | null>(null);

  const stepKey = computed(() => `whatsNew.club.step${step.value}`);
  const isLastStep = computed(() => step.value === TOTAL_STEPS);
  const closeLabel = computed(() => String(proxy.$t('whatsNew.close')));

  // Copy stays mounted (no v-if / :key under the plugin's Transition); the
  // fade is replayed by re-adding the animation class on the next frame.
  watch(step, () => {
    copyFading.value = false;
    requestAnimationFrame(() => {
      copyFading.value = true;
    });
  });

  function back() {
    if (step.value > 1) step.value--;
  }

  function next() {
    if (isLastStep.value) goToClub();
    else step.value++;
  }

  function dismiss() {
    modals.hide(WHATS_NEW_MODAL_NAME);
  }

  function goToClub() {
    const stringId = restaurantStore.restaurant?.stringId;
    // The host's `closed` handler persists; after hide the component
    // unmounts — no reactive writes past this point (renderer safety).
    modals.hide(WHATS_NEW_MODAL_NAME);
    if (stringId) {
      proxy.$router.push(`/${stringId}/club`).catch(() => {
        /* duplicated navigation */
      });
    }
  }

  function onKeyup(event: KeyboardEvent) {
    // clickToClose is false, so the plugin never binds Escape itself.
    if (event.key === 'Escape') {
      dismiss();
      return;
    }
    if (event.key === 'Enter') {
      // Focused controls already handle Enter natively (keydown click).
      const target = event.target as HTMLElement | null;
      if (target?.closest('button, a, input, textarea, select')) return;
      next();
    }
  }

  onMounted(() => {
    document.addEventListener('keyup', onKeyup);
    // The plugin's focusTrap focuses the first node (the X) after open —
    // move focus to the primary action once that has happened.
    setTimeout(() => {
      const el = primaryButton.value?.$el as HTMLElement | undefined;
      el?.focus?.();
    }, 250);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keyup', onKeyup);
  });
</script>

<template>
  <div
    class="v2-club-whats-new"
    role="dialog"
    aria-modal="true"
    aria-labelledby="whats-new-title"
  >
    <div class="modal-top">
      <span class="top-chip">{{ $t('whatsNew.chip') }}</span>
      <button
        class="top-close"
        type="button"
        :aria-label="closeLabel"
        @click="dismiss"
      >
        <PosUiIcon name="close" :size="20" />
      </button>
    </div>

    <div class="modal-body">
      <div class="scene-well">
        <div class="scene" :class="{ 'is-active': step === 1 }" v-show="step === 1">
          <V2WhatsNewClubSceneMenu />
        </div>
        <div class="scene" :class="{ 'is-active': step === 2 }" v-show="step === 2">
          <V2WhatsNewClubSceneRewards />
        </div>
        <div class="scene" :class="{ 'is-active': step === 3 }" v-show="step === 3">
          <V2WhatsNewClubSceneCode />
        </div>
      </div>

      <div class="copy-block" :class="{ 'is-fading': copyFading }">
        <h2 id="whats-new-title" class="copy-title">
          {{ $t(`${stepKey}.title`) }}
        </h2>
        <p class="copy-body">{{ $t(`${stepKey}.body`) }}</p>
        <p class="copy-hint" v-show="step === 3">
          {{ $t('whatsNew.club.step3.hint') }}
        </p>
      </div>
    </div>

    <div class="modal-footer">
      <div class="footer-dots" aria-hidden="true">
        <span
          v-for="dot in TOTAL_STEPS"
          :key="dot"
          class="dot"
          :class="{ 'is-active': dot === step }"
        ></span>
      </div>
      <PosUiButton
        class="footer-dismiss"
        color="neutral"
        variant="ghost"
        @click="dismiss"
      >
        {{ $t('whatsNew.dismiss') }}
      </PosUiButton>
      <span class="footer-spring"></span>
      <PosUiButton
        class="footer-back"
        :class="{ 'is-hidden': step === 1 }"
        color="neutral"
        variant="outline"
        :disabled="step === 1"
        :aria-hidden="step === 1"
        @click="back"
      >
        {{ $t('whatsNew.back') }}
      </PosUiButton>
      <PosUiButton
        ref="primaryButton"
        class="footer-primary"
        color="primary"
        @click="next"
      >
        {{ isLastStep ? $t('whatsNew.club.step3.cta') : $t('whatsNew.next') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-club-whats-new {
    @apply tw-flex tw-w-full tw-flex-col tw-bg-white;
    animation: whats-new-enter 160ms ease-out;

    .modal-top {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-px-5 tw-pb-1 tw-pt-4;

      .top-chip {
        @apply tw-rounded-full tw-bg-orange-50 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-orange-700;
      }

      .top-close {
        @apply tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-500 tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-100 tw-text-neutral-700;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .modal-body {
      @apply tw-flex tw-flex-col tw-gap-4 tw-px-5 tw-pb-2 tw-pt-1;

      .scene-well {
        @apply tw-relative tw-h-28 tw-flex-none tw-overflow-hidden tw-rounded-xl tw-bg-canvas;

        .scene {
          @apply tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center;

          &.is-active {
            animation: whats-new-scene 150ms ease-out;
          }
        }
      }

      .copy-block {
        @apply tw-flex tw-flex-col tw-gap-2;

        &.is-fading {
          animation: whats-new-scene 150ms ease-out;
        }

        .copy-title {
          @apply tw-m-0 tw-text-xl tw-font-semibold tw-leading-snug tw-text-neutral-900;
        }

        .copy-body {
          @apply tw-m-0 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
        }

        .copy-hint {
          @apply tw-m-0 tw-text-xs tw-leading-relaxed tw-text-neutral-400;
        }
      }
    }

    .modal-footer {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-gap-2 tw-px-5 tw-pb-4 tw-pt-3;

      .footer-dots {
        @apply tw-flex tw-items-center tw-gap-1.5;

        .dot {
          @apply tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-200 tw-transition-all tw-duration-150;

          &.is-active {
            @apply tw-w-4 tw-bg-orange-600;
          }
        }
      }

      .footer-spring {
        @apply tw-flex-1;
      }

      .footer-back.is-hidden {
        visibility: hidden;
      }
    }
  }

  /* Desktop / tablet: split scene + copy, dots inline between buttons. */
  @media (min-width: 768px) {
    .v2-club-whats-new {
      .modal-body {
        @apply tw-flex-row tw-items-stretch tw-gap-5;

        .scene-well {
          @apply tw-h-[280px] tw-w-[280px];
        }

        .copy-block {
          @apply tw-flex-1 tw-justify-center;
        }
      }

      .modal-footer {
        .footer-dots {
          @apply tw-order-3 tw-px-1;
        }

        .footer-dismiss {
          @apply tw-order-1;
        }

        .footer-spring {
          @apply tw-order-2;
        }

        .footer-back {
          @apply tw-order-2;
        }

        .footer-primary {
          @apply tw-order-4;
        }
      }
    }
  }

  /* Phone: dots on their own row above the buttons. */
  @media (max-width: 767px) {
    .v2-club-whats-new {
      .modal-footer {
        .footer-dots {
          @apply tw-w-full tw-justify-center tw-pb-1;
        }
      }
    }
  }

  /* Short viewports: hide the scene, keep copy + footer (CSS only). */
  @media (max-height: 540px) {
    .v2-club-whats-new {
      .modal-body {
        .scene-well {
          display: none;
        }
      }
    }
  }

  @keyframes whats-new-enter {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes whats-new-scene {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-club-whats-new {
      animation-name: whats-new-scene;
    }
  }
</style>
